import moment from "moment";
import {getDateRangeByLabel} from "../../components/DateRange";

const LEGACY_BOOL_FILTER_KEYS = [
    "new_school", "creative", "exclusive", "medium", "cf", "cw", "already",
    "undecided_bach", "cert_upgrade", "when_start_upgrade", "military",
] as const;

export type LinkoutPresetSnapshot = {
    dates: Date[];
    data: any[];
    stats: any[];
    filterItems: any[];
    uniqueLeads: boolean;
    minResults: string;
    contactRate: string;
    transferRate: string;
    selectedBrokers: any[];
    selectedPublishers: any[];
    publishersExclude: boolean;
    selectedSchools: any[];
    selectedCompanies: any[];
    selectedPrograms: any[];
    selectedSubjects: any[];
    selectedTraffic: any[];
    selectedWhenStart: any[];
    selectedWorking: any[];
    selectedCredits: any[];
    selectedEdu: any[];
    selectedDegree: any[];
    selectedClasses: any[];
    selectedConvTypes: any[];
    boolFilters: Record<string, boolean>;
    campaignIds: string;
    creativeUrls: string;
    states: string;
    gender: string;
    hoursOldMin: string;
    hoursOldMax: string;
    ageMin: string;
    ageMax: string;
};

type NormalizeOptions = {
    dataOptions: { id: string; value: string }[];
    statOptions: { id: string; value: string }[];
    filterOptions: { id: string; value: string }[];
    brokers: { id: number; value: string }[];
    schools: { id: number; value: string }[];
    companies: { id: string; value: string }[];
    programs: { id: number; value: string }[];
    subjects: { id: number; value: string }[];
    trafficTypes: { id: string; value: string }[];
    whenStart: { id: number; value: string }[];
    working: { id: number; value: string }[];
    credits?: { id: number; value: string }[];
    eduLevels: { id: number; value: string }[];
    degreeLevels: { id: number; value: string }[];
    classes: { id: number; value: string }[];
    conversionTypes: { id: number; value: string }[];
};

function idsFrom(raw: any): number[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : String(raw).split(/[,\s]+/);
    return list.map((x) => parseInt(String(typeof x === "object" && x != null ? x.id : x), 10)).filter((n) => !isNaN(n));
}

/** Coerce numeric string ids (common in legacy presets) to numbers; leave non-numeric strings alone. */
function coerceId(id: any): number | string {
    if (typeof id === "number" && !isNaN(id)) return id;
    if (typeof id === "string" && /^-?\d+$/.test(id.trim())) return parseInt(id.trim(), 10);
    if (id != null && typeof id !== "object") {
        const n = Number(id);
        if (!isNaN(n) && String(id).trim() !== "") return n;
    }
    return id;
}

function pickByIds<T extends { id: number | string }>(all: T[], raw: any, fallbackLabel = "id"): T[] {
    const idList = Array.isArray(raw)
        ? raw.map((x) => (typeof x === "object" && x != null ? x.id : x))
        : idsFrom(raw);
    return idList.map((id) => {
        const coerced = coerceId(id);
        const found = all.find((x) => String(x.id) === String(coerced));
        return found || ({id: coerced, value: String(id)} as unknown as T);
    });
}

/** Deduplicate MultiSelect / preset items by id (first wins). */
export function uniqueById<T extends {id: string | number}>(items: T[]): T[] {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const item of items || []) {
        const key = String(item.id);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(item);
    }
    return out;
}

/** Stable-sort items by preferred id order; unknown ids keep relative order at the end. */
export function orderByIds<T extends {id: string | number}>(items: T[], preferredOrder: string[]): T[] {
    const rank = new Map(preferredOrder.map((id, i) => [id, i]));
    return [...(items || [])].sort((a, b) => {
        const ra = rank.has(String(a.id)) ? rank.get(String(a.id))! : preferredOrder.length + 1;
        const rb = rank.has(String(b.id)) ? rank.get(String(b.id))! : preferredOrder.length + 1;
        return ra - rb;
    });
}

export function uniqueOrderedByIds<T extends {id: string | number}>(items: T[], preferredOrder: string[]): T[] {
    return orderByIds(uniqueById(items), preferredOrder);
}

/**
 * Split combined campaign display "221075 (CW - Purdue Global Business New)"
 * into Campaign ID + App ID for CSV export.
 */
export function splitCampaignExportValue(value: unknown): {campaignId: string; appId: string} {
    const raw = String(value ?? "").trim();
    const withApp = raw.match(/^(\d+)\s*\((.*)\)\s*$/);
    if (withApp) return {campaignId: withApp[1], appId: withApp[2]};
    const idOnly = raw.match(/^(\d+)\s*$/);
    if (idOnly) return {campaignId: idOnly[1], appId: ""};
    return {campaignId: "", appId: raw};
}

/** Expand "Campaign" into separate Campaign ID / App ID columns for export rows. */
export function expandExportDataColumns(
    displayData: {id: string; value: string}[],
    columnValue: (row: any, key: string) => string,
    row: any,
): {headers: string[]; values: string[]} {
    const headers: string[] = [];
    const values: string[] = [];
    for (const d of displayData) {
        if (d.id === "campaign") {
            const {campaignId, appId} = splitCampaignExportValue(columnValue(row, d.id));
            headers.push("Campaign ID", "App ID");
            values.push(campaignId, appId);
        } else {
            headers.push(d.value);
            values.push(String(columnValue(row, d.id) ?? ""));
        }
    }
    return {headers, values};
}

/** Prefer Year before Month in data column order (DATA_OPTIONS are alphabetical). */
export function yearBeforeMonthOrder(ids: string[]): string[] {
    const out = [...ids];
    const yi = out.indexOf("year");
    const mi = out.indexOf("month");
    if (yi >= 0 && mi >= 0 && yi > mi) {
        out.splice(yi, 1);
        out.splice(mi, 0, "year");
    }
    return out;
}

/** Map MultiSelect items to GraphQL [Int!] values. */
export function toIntIds(items: any[]): number[] {
    return (items || [])
        .map((x) => Number(typeof x === "object" && x != null ? x.id : x))
        .filter((n) => !isNaN(n));
}

function parsePresetDates(dates: any): Date[] | null {
    if (Array.isArray(dates) && dates.length === 2) {
        return [moment(dates[0]).toDate(), moment(dates[1]).toDate()];
    }
    const range = dates?.selectedRange ? String(dates.selectedRange) : "";
    // Legacy recomputes named ranges on run; only Custom Range uses saved start/end.
    if (range && range !== "Custom Range") {
        const fromLabel = getDateRangeByLabel(range);
        if (fromLabel) return fromLabel;
    }
    if (dates?.selectedStartDate != null && dates?.selectedEndDate != null) {
        return [moment(dates.selectedStartDate).toDate(), moment(dates.selectedEndDate).toDate()];
    }
    return null;
}

function parseCompanies(raw: any, companies: { id: string; value: string }[]): any[] {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.map((x) => {
            const name = typeof x === "object" ? x.id ?? x.name ?? x.value : x;
            const found = companies.find((c) => c.id === name || c.value === name);
            return found || {id: String(name), value: String(name)};
        });
    }
    const names = String(raw).split(",").map((x) => x.trim()).filter(Boolean);
    return names.map((name) => companies.find((c) => c.id === name || c.value === name) || {id: name, value: name});
}

function publisherItems(raw: any, legacy: boolean, f: any): any[] {
    const ids = legacy
        ? idsFrom(f.publishers)
        : (Array.isArray(raw.selectedPublishers)
            ? raw.selectedPublishers.map((x: any) => (typeof x === "object" && x != null ? x.id : x))
            : idsFrom(raw.selectedPublishers));
    return ids.map((id: any) => {
        const coerced = coerceId(id);
        return {id: typeof coerced === "number" ? coerced : Number(id), value: String(id)};
    }).filter((x: any) => !isNaN(x.id));
}

/** Map legacy Angular preset JSON (filters + dates object) into native LO page shape. */
export function normalizeConversionStatPreset(raw: any, opts: NormalizeOptions): LinkoutPresetSnapshot {
    const legacy = !Array.isArray(raw?.dates);
    const f = raw?.filters || {};

    const boolFilters: Record<string, boolean> = {...(raw.boolFilters || {})};
    LEGACY_BOOL_FILTER_KEYS.forEach((key) => {
        if (legacy && f[key] != null) boolFilters[key] = !!f[key];
    });

    let gender = raw.gender ?? "";
    if (legacy && f.gender != null && typeof f.gender === "boolean") {
        gender = "";
    } else if (legacy && typeof f.gender === "string") {
        gender = f.gender;
    }

    const dates = parsePresetDates(raw.dates) || [
        moment().startOf("month").toDate(),
        moment().endOf("day").toDate(),
    ];

    const dataIds = uniqueById((raw.data || []).map((id: any) => ({id: String(id)}))).map((x) => String(x.id));
    const statsIds = uniqueById((raw.stats || []).map((id: any) => ({id: String(id)}))).map((x) => String(x.id));
    const dataOrder = yearBeforeMonthOrder(opts.dataOptions.map((x) => x.id));
    const statsOrder = opts.statOptions.map((x) => x.id);

    return {
        dates,
        data: uniqueOrderedByIds(
            opts.dataOptions.filter((x) => dataIds.includes(x.id)),
            dataOrder,
        ),
        stats: uniqueOrderedByIds(
            opts.statOptions
                .filter((x) => statsIds.includes(x.id))
                .map((x) => ({id: x.id, value: x.value})),
            statsOrder,
        ),
        filterItems: opts.filterOptions.filter((x) => (raw.filterItems || []).includes(x.id)),
        uniqueLeads: raw.uniqueLeads != null ? !!raw.uniqueLeads : true,
        minResults: String(raw.minResults ?? f.total ?? "1"),
        contactRate: String(raw.contactRate ?? f.contactRate ?? "0.01"),
        transferRate: String(raw.transferRate ?? f.transferRate ?? "0.01"),
        selectedBrokers: pickByIds(opts.brokers, legacy ? f.brokers : raw.selectedBrokers),
        selectedPublishers: publisherItems(raw, legacy, f),
        publishersExclude: !!(raw.publishersExclude ?? f?.publishers_exclude),
        selectedSchools: pickByIds(opts.schools, legacy ? f.schools : raw.selectedSchools),
        selectedCompanies: parseCompanies(legacy ? f.companies : raw.selectedCompanies, opts.companies),
        selectedPrograms: pickByIds(opts.programs, legacy ? f.programs : raw.selectedPrograms),
        selectedSubjects: pickByIds(opts.subjects, legacy ? f.subjects : raw.selectedSubjects),
        selectedTraffic: pickByIds(opts.trafficTypes, legacy ? f.traffic_types : raw.selectedTraffic),
        selectedWhenStart: pickByIds(opts.whenStart, legacy ? f.when_start : raw.selectedWhenStart),
        selectedWorking: pickByIds(opts.working, legacy ? f.working : raw.selectedWorking),
        selectedCredits: pickByIds(opts.credits || [], legacy ? f.credits : raw.selectedCredits),
        selectedEdu: pickByIds(opts.eduLevels, legacy ? f.edu_level : raw.selectedEdu),
        selectedDegree: pickByIds(opts.degreeLevels, legacy ? f.degree_level : raw.selectedDegree),
        selectedClasses: pickByIds(opts.classes, legacy ? f.classes : raw.selectedClasses),
        selectedConvTypes: pickByIds(opts.conversionTypes, legacy ? f.conversion_type : raw.selectedConvTypes),
        boolFilters,
        campaignIds: String(legacy ? (f.campaigns ?? "") : (raw.campaignIds ?? "")),
        creativeUrls: String(legacy ? (f.creative_ids ?? "") : (raw.creativeUrls ?? "")),
        states: String(legacy ? (f.states ?? "") : (raw.states ?? "")),
        gender: String(gender),
        hoursOldMin: String(raw.hoursOldMin ?? f?.hours_old_min ?? "0"),
        hoursOldMax: String(raw.hoursOldMax ?? f?.hours_old_max ?? "72"),
        ageMin: String(raw.ageMin ?? f?.age_min ?? "18"),
        ageMax: String(raw.ageMax ?? f?.age_max ?? "99"),
    };
}

export type ConversionStatPresetRow = {
    id: number;
    name: string;
    repId: number;
    isLinkOut?: boolean;
    sharedRepIds?: number[];
    preset?: string;
};

function humanizeToken(value: string): string {
    return String(value || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
}

function listFromRaw(raw: any, max = 8): string {
    if (raw == null || raw === "") return "";
    const items = Array.isArray(raw)
        ? raw.map((x) => (typeof x === "object" && x != null ? String(x.value ?? x.name ?? x.id ?? "") : String(x)))
        : String(raw).split(/[,\n]+/);
    const labels = items.map((x) => humanizeToken(String(x).trim())).filter(Boolean);
    if (!labels.length) return "";
    const shown = labels.slice(0, max);
    const extra = labels.length - shown.length;
    return extra > 0 ? `${shown.join(", ")} +${extra} more` : shown.join(", ");
}

function formatPresetDateRange(dates: any): string {
    if (Array.isArray(dates) && dates.length === 2) {
        return `${moment(dates[0]).format("MM/DD/YYYY h:mm A")} – ${moment(dates[1]).format("MM/DD/YYYY h:mm A")}`;
    }
    const range = dates?.selectedRange ? String(dates.selectedRange) : "";
    if (range && range !== "Custom Range") return range;
    if (dates?.selectedStartDate != null && dates?.selectedEndDate != null) {
        const label = range ? `${range}: ` : "";
        return `${label}${moment(dates.selectedStartDate).format("MM/DD/YYYY")} – ${moment(dates.selectedEndDate).format("MM/DD/YYYY")}`;
    }
    return "";
}

/** Lines shown on the delete-preset confirm dialog. */
export function conversionStatPresetDeleteDetails(
    row: ConversionStatPresetRow,
    currentUserId?: number | null,
): {name: string; lines: {label: string; value: string}[]; shareWarning?: string} {
    const lines: {label: string; value: string}[] = [
        {label: "ID", value: String(row.id)},
        {label: "Report", value: row.isLinkOut ? "Conversion Stats Linkout" : "Conversion Stats"},
        {
            label: "Owner",
            value: currentUserId != null && row.repId === currentUserId ? "You" : `Rep ${row.repId}`,
        },
    ];
    const sharedCount = (row.sharedRepIds || []).length;
    if (sharedCount) {
        lines.push({label: "Shared with", value: `${sharedCount} ${sharedCount === 1 ? "person" : "people"}`});
    }

    try {
        const raw = row.preset ? JSON.parse(row.preset) : {};
        const f = raw.filters || {};
        const dateRange = formatPresetDateRange(raw.dates);
        if (dateRange) lines.push({label: "Date range", value: dateRange});
        const data = listFromRaw(raw.data);
        if (data) lines.push({label: "Data", value: data});
        const stats = listFromRaw(raw.stats);
        if (stats) lines.push({label: "Statistics", value: stats});
        if (raw.uniqueLeads != null) {
            lines.push({label: "Unique leads", value: raw.uniqueLeads ? "Yes" : "No"});
        }
        const brokers = listFromRaw(raw.selectedBrokers ?? f.brokers);
        if (brokers) lines.push({label: "Brokers", value: brokers});
        const publishers = listFromRaw(raw.selectedPublishers ?? f.publishers);
        if (publishers) {
            lines.push({
                label: (raw.publishersExclude ?? f.publishers_exclude) ? "Publishers (exclude)" : "Publishers",
                value: publishers,
            });
        }
        const schools = listFromRaw(raw.selectedSchools ?? f.schools);
        if (schools) lines.push({label: "Schools", value: schools});
        const companies = listFromRaw(raw.selectedCompanies ?? f.companies);
        if (companies) lines.push({label: "Companies", value: companies});
        const campaigns = String(raw.campaignIds ?? f.campaigns ?? "").trim();
        if (campaigns) lines.push({label: "Campaign IDs", value: campaigns});
    } catch {
        // Stored JSON can be malformed; still confirm by name/id.
    }

    const shareWarning = sharedCount
        ? `This preset is shared with ${sharedCount} ${sharedCount === 1 ? "person" : "people"}. They will lose access.`
        : undefined;

    return {name: row.name || "Untitled preset", lines, shareWarning};
}
